import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { useCopyLink } from '@/lib/utils/useCopyLink';
import { useI18nText } from '@/lib/utils/i18nText';
import { usePreviewMode } from '@/contexts/PreviewModeContext';
import { detectBeTargetEnvFromBrowser, type BeTargetEnv } from '@/lib/config/runtimeEnv';
import { PopoverShell } from '@/components/PopoverShell';

/**
 * v1 (gc-mono) admin origins per BE environment. The button reuses the current
 * page's path on the matching v1 host so admins can compare v1 vs v2 until v1 is
 * retired. Origins are fixed per environment (NOT a `control2`→`control` host
 * swap) so localhost/staging point at the correct v1 deployment instead of a
 * self-referential no-op. Picked via the v2 BE target, so a localhost dev build
 * follows whichever BE (staging/live) it is wired to.
 *
 * `live` points at `control1` ahead of the planned domain swap: once v2 goes
 * live on the canonical `control.ggcontent.com`, legacy v1 moves to
 * `control1.ggcontent.com` — this is pre-set for that cutover.
 */
const V1_ORIGIN: Record<BeTargetEnv, string> = {
  live: 'https://control1.ggcontent.com',
  staging: 'https://gmchanger-admin-dev.herokuapp.com',
};

/**
 * Only the campaign / influencer / payment areas surface the compare button —
 * the three areas the user asked for, where a v1 counterpart is worth diffing.
 * Kept as one flat list so the scope is easy to eyeball and trim. Each entry
 * matches the pathname exactly or as a `<prefix>/…` sub-path. Some v2-only pages
 * have no v1 equivalent (their v1 URL will 404) — that is accepted; this is a
 * compare aid, not a guaranteed 1:1 mapping.
 */
const V1_COMPARE_PATH_PREFIXES = [
  // Campaign area
  '/campaigns', '/dashboard/campaign', '/summary', '/campaign_post_candidates',
  '/analytics', '/stats', '/candidates', '/communications', '/videos',
  // Influencer area
  '/influencers', '/influencer', '/import_influencers', '/blacklist', '/rosters',
  '/general', '/panel/user',
  // Payment area
  '/payments', '/finances',
];

function isV1CompareArea(pathname: string): boolean {
  return V1_COMPARE_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/**
 * v2 → v1 path overrides for pages whose route was renamed between versions.
 * Most pages keep the same path on v1, so only the exceptions are listed here.
 * Matched against the EXACT pathname (sub-paths are intentionally not rewritten —
 * they may be v2-only, e.g. `/influencers/add` has no `/dashboard/add` on v1).
 * The influencer dashboard is `/influencers` on v2 but `/dashboard` on v1.
 */
const V1_PATH_OVERRIDES: Record<string, string> = {
  '/influencers': '/dashboard',
};

function toV1Path(pathname: string): string {
  return V1_PATH_OVERRIDES[pathname] ?? pathname;
}

/**
 * Floating "compare with v1" helper pinned to the viewport's bottom-right (the
 * footer's right end). Copies / opens the current page's v1 address.
 *
 * Visibility gate (all must hold): the account is a real admin, we are NOT in the
 * advertiser preview overlay, and the route is a campaign/influencer/payment page.
 * This mirrors CampaignNavTabs' `role === 'admin' && !isPreview` gate so the tool
 * never leaks into an advertiser-facing view.
 *
 * Click-to-open popover (PopoverShell, same shared shell as every other
 * anchored popover in the app) instead of a hover panel — the fab used to copy
 * the address on its own click (hover only revealed the panel as a bonus), which
 * meant a plain click had no way to choose "open in new tab" instead, and touch
 * devices have no hover at all to reveal the panel in the first place.
 */
export function V1CompareButton() {
  const { pathname } = useLocation();
  const role = useAppSelector((s) => s.envs.currentUser?.role);
  const isPreview = usePreviewMode();
  const copyLink = useCopyLink();
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);

  if (role !== 'admin' || isPreview) return null;
  if (!isV1CompareArea(pathname)) return null;

  // Path only — v2 query params / hash (?status=, ?page=, …) are v2-era and don't
  // map onto v1, so carrying them over would land v1 on a misleading filtered
  // state. The path identifies the entity (campaignId/token live in the path).
  const v1Url = V1_ORIGIN[detectBeTargetEnvFromBrowser()] + toV1Path(pathname);

  const copy = () => {
    copyLink(v1Url, t('v1 address copied', 'v1 주소가 복사되었습니다'));
    setOpen(false);
  };
  const openInNewTab = () => {
    window.open(v1Url, '_blank', 'noopener,noreferrer');
    setOpen(false);
  };

  return (
    <div className="v1-compare font-ggc">
      <PopoverShell
        open={open}
        onOpenChange={setOpen}
        trigger={(
          <button
            type="button"
            className="v1-compare__fab"
            aria-label={t('Compare with v1', 'v1과 비교')}
          >
            <i aria-hidden className="fa fa-columns" />
            <span className="v1-compare__fab-label">v1</span>
          </button>
        )}
        side="top"
        align="end"
        width={264}
        className="v1-compare-popover"
      >
        <div className="v1-compare__title">
          <i aria-hidden className="fa fa-columns" /> {t('Compare with v1', 'v1과 비교')}
        </div>
        <p className="v1-compare__desc">
          {t(
            'Until v1 is retired, copy the v1 address of this page to compare it with the new version.',
            'v1이 종료되기 전까지 신규 버전과 비교할 수 있도록 이 페이지의 v1 주소를 복사할 수 있습니다.',
          )}
        </p>
        <div className="v1-compare__url">{v1Url}</div>
        <div className="v1-compare__actions">
          <button type="button" className="v1-compare__action" onClick={copy}>
            <i aria-hidden className="fa fa-files-o" /> {t('Copy address', '주소 복사')}
          </button>
          <button type="button" className="v1-compare__action" onClick={openInNewTab}>
            <i aria-hidden className="fa fa-external-link" /> {t('Open in new tab', '새 창으로 열기')}
          </button>
        </div>
      </PopoverShell>
    </div>
  );
}
